import { ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/misc";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { timeRange } from "@/lib/appointments";
import {
  browserZone, customProblems, otherClocks, wallTime, withDay, withFrom, withMode, withSlot, withTo, withZone, zoneChoices,
  zoneLabel, type TimeMode, type When,
} from "@/lib/booking-time";
import { viewRange } from "@/lib/calendar-view";
import { JS_WEEKDAY, addDays, dayLabel, localDay, slotTime } from "@/lib/calendars";
import { useCalendarSlots } from "@/lib/queries";
import type { Calendar } from "@/lib/types";
import { cn } from "@/lib/utils";

/** A local date as `YYYY-MM-DD`. */
export const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/**
 * When a meeting is booked or moved to (ACL-292, ACL-521). The times read in a timezone the booker
 * picks: the calendar's to start with, their computer's, the contact's or another. Then either a
 * date from a month of the calendar's days — the ones with no free time greyed — and a time from
 * that day's free slots (Default), or a start and end of the booker's own (Custom, ADR-169 in
 * platform), refused only when a host is busy then. Every time is held as an instant
 * (`lib/booking-time.ts`), so a change of zone never moves the time picked.
 *
 * Booking and rescheduling both pick a time with it; a reschedule passes `ignore` so the
 * appointment's own time is free. `userId` narrows a round-robin calendar's times to one staff
 * member's (ACL-290). A class calendar offers no custom time: its sessions are joined at its own.
 */
export function DayAndTime({
  id, calendar, when, onWhen, wanted = null, ignore, userId, contactZone,
}: {
  /** The zone picker's id, for the field around it. */
  id?: string;
  calendar: Calendar;
  when: When;
  onWhen: (change: (w: When) => When) => void;
  /** The time clicked on the calendar: its slot is chosen once the day's slots are in. */
  wanted?: Date | null;
  /** The appointment being rescheduled. */
  ignore?: number;
  /** Only the times this staff member is free; any staff member when empty. */
  userId?: number | null;
  /** The contact's own clock, offered as a zone and shown beside the time picked. */
  contactZone?: string | null;
}) {
  const zone = when.zone;
  const duration = calendar.duration_minutes;
  const allowCustom = calendar.calendar_type !== "class";
  const mode: TimeMode = allowCustom ? when.mode : "default";
  const today = localDay(new Date().toISOString(), zone);
  const [month, setMonth] = React.useState(() => firstOfMonth(when.day ?? today));
  const [picking, setPicking] = React.useState(false);
  // The calendar's own first day of the week (Booking rules → Week starts on).
  const grid = viewRange("month", month, JS_WEEKDAY[calendar.week_starts_on]).days;
  const first = ymd(grid[0]!) < today ? today : ymd(grid[0]!);
  const last = ymd(grid.at(-1)!);
  // core-api reads the days on the calendar's clock. On another zone, a day either side holds the
  // slots that fall on the grid's first and last days there.
  const pad = zone === calendar.timezone ? 0 : 1;
  const slots = useCalendarSlots(
    calendar.id,
    addDays(first, -pad),
    addDays(last, pad),
    { enabled: mode === "default" && last >= today },
    ignore,
  );
  const free = new Map<string, string[]>();
  // A class session's seats left at each time (ACL-293); absent on the other types.
  const seats = new Map<string, number>();
  const ends = new Map<string, string>();
  for (const s of slots.data?.slots ?? []) {
    if (userId != null && !s.user_ids.includes(userId)) continue;
    if (s.seats_left != null) seats.set(s.start, s.seats_left);
    ends.set(s.start, s.end);
    const d = localDay(s.start, zone);
    free.set(d, [...(free.get(d) ?? []), s.start]);
  }
  const times = when.day ? (free.get(when.day) ?? []) : [];
  // The first free slot in the half hour clicked; none there leaves the time to pick.
  const wantedSlot = wanted
    ? times.find((t) => {
        const ms = new Date(t).getTime() - wanted.getTime();
        return ms >= 0 && ms < 30 * 60_000;
      })
    : undefined;
  React.useEffect(() => {
    if (wantedSlot) onWhen((w) => (w.slot ? w : withSlot(w, wantedSlot)));
    // Once, when the slots arrive: a time picked by hand afterwards is left alone.
  }, [wantedSlot]);

  const choices = React.useMemo(() => {
    const list = zoneChoices(calendar.timezone, browserZone(), contactZone);
    return list.some((c) => c.zone === zone) ? list : [...list, { zone, whose: [] }];
  }, [calendar.timezone, contactZone, zone]);
  const monthName = new Intl.DateTimeFormat(calendar.locale, { month: "long", year: "numeric" }).format(month);
  const problems = mode === "custom" ? customProblems(when, Date.now()) : {};
  const shown = mode === "default"
    ? when.slot && { start: when.slot, end: ends.get(when.slot) ?? plusMinutes(when.slot, duration) }
    : when.from && when.to && !problems.from && !problems.to && { start: when.from, end: when.to };

  return (
    <div className="space-y-3 rounded-lg bg-gray-2 p-3">
      <div className="space-y-1.5">
        <label htmlFor={id ?? "when-zone"} className="text-xs text-text-muted">
          Showing times in this timezone
        </label>
        <Select value={zone} onValueChange={(z) => onWhen((w) => withZone(w, z))}>
          <SelectTrigger id={id ?? "when-zone"} className="bg-surface">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {choices.map((c) => (
              <SelectItem key={c.zone} value={c.zone}>
                {zoneLabel(c.zone, Date.now(), calendar.locale)}
                {c.whose.length > 0 && <span className="ml-1.5 text-xs text-text-muted">· {c.whose.join(", ")}</span>}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {allowCustom && (
        <Tabs value={mode} onValueChange={(m) => onWhen((w) => withMode(w, m as TimeMode, duration, Date.now()))}>
          <TabsList>
            <TabsTrigger value="default">Default</TabsTrigger>
            <TabsTrigger value="custom">Custom</TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {mode === "default" ? (
        <div className="grid gap-2 sm:grid-cols-2">
          <Popover
            open={picking}
            onOpenChange={(open) => {
              // Open on the month of the day picked: another zone can move it across a month's end.
              if (open && when.day) setMonth(firstOfMonth(when.day));
              setPicking(open);
            }}
          >
            <PopoverTrigger asChild>
              <Button type="button" variant="outline" className="justify-start bg-surface font-normal">
                {when.day ? dayLabel(when.day, calendar.locale) : "Pick a date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-3" align="start">
              <div className="mb-2 flex items-center justify-between">
                <Button type="button" variant="ghost" size="icon-sm" aria-label="Previous month" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}>
                  <ChevronLeft />
                </Button>
                <span className="text-sm font-medium text-text">{monthName}</span>
                <Button type="button" variant="ghost" size="icon-sm" aria-label="Next month" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}>
                  <ChevronRight />
                </Button>
              </div>
              <div className="grid grid-cols-7 gap-0.5 text-center">
                {grid.slice(0, 7).map((d) => (
                  <span key={d.getDay()} className="py-1 text-2xs text-text-subtle">
                    {new Intl.DateTimeFormat(calendar.locale, { weekday: "short" }).format(d)}
                  </span>
                ))}
                {grid.map((d) => {
                  const key = ymd(d);
                  const bookable = free.has(key);
                  const inMonth = d.getMonth() === month.getMonth();
                  return (
                    <button
                      key={key}
                      type="button"
                      disabled={!bookable}
                      onClick={() => {
                        onWhen((w) => withDay(w, key));
                        setPicking(false);
                      }}
                      className={cn(
                        "rounded-md py-1.5 text-xs",
                        key === when.day ? "bg-accent-9 text-white" : bookable ? "text-text hover:bg-accent-3" : "text-text-subtle",
                        !inMonth && "opacity-50",
                        key === today && key !== when.day && "font-semibold text-accent-11",
                      )}
                    >
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-2xs text-text-subtle">{slots.isFetching ? "Finding free times…" : "Greyed days have no free time."}</p>
            </PopoverContent>
          </Popover>
          <Select value={when.slot ?? ""} onValueChange={(s) => onWhen((w) => withSlot(w, s))} disabled={!when.day || times.length === 0}>
            <SelectTrigger aria-label="Time" className="bg-surface">
              <SelectValue placeholder={when.day && times.length === 0 ? "No free times" : "Pick a time"} />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {times.map((t) => (
                <SelectItem key={t} value={t}>
                  {slotTime(t, zone, calendar.time_format, calendar.locale)}
                  {seats.has(t) && (
                    <span className="ml-1.5 text-xs text-text-muted">
                      · {seats.get(t)} seat{seats.get(t) === 1 ? "" : "s"} left
                    </span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="grid gap-2 sm:grid-cols-2">
            <Field label="Start time" htmlFor="when-from" error={problems.from}>
              <Input
                id="when-from"
                type="datetime-local"
                className="bg-surface"
                value={when.from ? wallTime(when.from, zone) : ""}
                invalid={Boolean(problems.from)}
                onChange={(e) => onWhen((w) => withFrom(w, e.target.value, duration))}
              />
            </Field>
            <Field label="End time" htmlFor="when-to" error={problems.to}>
              <Input
                id="when-to"
                type="datetime-local"
                className="bg-surface"
                value={when.to ? wallTime(when.to, zone) : ""}
                min={when.from ? wallTime(when.from, zone) : undefined}
                invalid={Boolean(problems.to)}
                onChange={(e) => onWhen((w) => withTo(w, e.target.value))}
              />
            </Field>
          </div>
          <p className="text-2xs text-text-subtle">
            Any time from now on, the calendar's hours or not. It is refused only when a host already has an appointment or
            blocked-off time then.
          </p>
        </div>
      )}

      {shown &&
        otherClocks(when, contactZone).map((o) => (
          <p key={o.zone} className="text-xs text-text-muted">
            {o.whose === "Calendar" ? "The calendar's time" : "The contact's time"}:{" "}
            {timeRange(shown.start, shown.end, o.zone, calendar.time_format, calendar.locale)}
          </p>
        ))}
    </div>
  );
}

function firstOfMonth(day: string): Date {
  const [y, m] = day.split("-").map(Number);
  return new Date(y!, m! - 1, 1);
}

const plusMinutes = (iso: string, minutes: number) => new Date(Date.parse(iso) + minutes * 60_000).toISOString();
